import { useCallback, useEffect, useState } from 'react';
import { PowerReRollStrategy, SimulationConfig } from '../../core/types';
import { Constants } from '../../core/constants';
import { Tooltip, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { useCompactLayout } from '../../hooks/useCompactLayout';
import SearchableSelect from '../../components/SearchableSelect';
import Button from '../../components/Button';

interface SimulatorConfigSectionProps {
    config: SimulationConfig;
    setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

const POWER_REROLL_STRATEGY_OPTIONS = [
    PowerReRollStrategy.TABLE_HEURISTIC,
    PowerReRollStrategy.LEGACY_HEURISTIC,
    PowerReRollStrategy.OPTIMAL_STRATEGY,
];

export const SimulatorConfigSection: React.FC<SimulatorConfigSectionProps> = ({ config, setConfig }) => {
    const isCompact = useCompactLayout();
    const [isExpanded, setIsExpanded] = useState(() => {
        const storedExpanded = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem(Constants.LocalStorageKeys.SIMULATOR_SETTINGS_EXPANDED) || 'null') : null;
        return typeof storedExpanded === 'boolean' ? storedExpanded : !isCompact;
    });

    useEffect(() => {
        localStorage.setItem(Constants.LocalStorageKeys.SIMULATOR_SETTINGS_EXPANDED, JSON.stringify(isExpanded));
    }, [isExpanded]);

    const handleConfigChange = useCallback(
        (field: keyof SimulationConfig, value: number | boolean | PowerReRollStrategy) => {
            if (field === 'powerReRollThreshold') {
                value = Math.max(value as number, 0);
                value = Math.min(value as number, 1);
            }
            setConfig((prev) => {
                if (prev[field] !== value) {
                    if (field === 'powerReRollThreshold') {
                        value = Math.round((value as number) * 100) / 100;
                    }
                    return { ...prev, [field]: value };
                }
                return prev;
            });
        },
        [setConfig],
    );

    return (
        <div className="input-section">
            <div className="panel-section-header">
                <div className="panel-section-heading">
                    <h3>Simulator Settings</h3>
                    {isCompact && <p className="panel-section-summary">5 toggles, 1 dropdown</p>}
                </div>
                <Button className="panel-collapse-button" variant="ghost" size="sm" aria-expanded={isExpanded} onClick={() => setIsExpanded((current) => !current)}>
                    {isExpanded ? 'Hide' : 'Show'}
                </Button>
            </div>
            <div className={`panel-section-body ${!isExpanded ? 'panel-section-body-collapsed' : ''}`}>
                <div className="input-group">
                    <Tooltip
                        title={
                            <pre className="tooltip-pre">
                                <strong>On:</strong> Adds 1 to the current Argonaut Rage before the simulator resolves the attack.
                                <br />
                                <strong>Off:</strong> Uses the Argonaut Rage value you entered as-is.
                            </pre>
                        }
                        arrow
                        enterDelay={500}
                        enterNextDelay={500}
                    >
                        <Typography className="input-label-with-tooltip">
                            Attack with Rage +1
                            <InfoIcon className="info-icon-small" />
                        </Typography>
                    </Tooltip>
                    <input type="checkbox" checked={config.autoIncrementRage} onChange={(e) => handleConfigChange('autoIncrementRage', e.target.checked)} />
                </div>
                <div className="input-group">
                    <Tooltip
                        title={
                            <pre className="tooltip-pre">
                                <strong>On:</strong> Skips the attack roll and goes straight to the power roll with the maximum possible hits.
                                <br />
                                <strong>Off:</strong> Resolves the attack roll normally against the Primordial&apos;s To-Hit value.
                            </pre>
                        }
                        arrow
                        enterDelay={500}
                        enterNextDelay={500}
                    >
                        <Typography className="input-label-with-tooltip">
                            Skip Attack Roll
                            <InfoIcon className="info-icon-small" />
                        </Typography>
                    </Tooltip>
                    <input type="checkbox" checked={config.skipAttackRoll} onChange={(e) => handleConfigChange('skipAttackRoll', e.target.checked)} />
                </div>
                <div className="input-group">
                    <Tooltip
                        title={
                            <pre className="tooltip-pre">
                                <strong>On:</strong> The simulator never spends attack re-rolls.
                                <br />
                                <strong>Off:</strong> The simulator re-rolls misses once, up to the loadout&apos;s attack re-roll limit. Armory loadouts also respect Fate, capped at 9.
                            </pre>
                        }
                        arrow
                        enterDelay={500}
                        enterNextDelay={500}
                    >
                        <Typography className="input-label-with-tooltip">
                            Disable Attack Re-Rolls
                            <InfoIcon className="info-icon-small" />
                        </Typography>
                    </Tooltip>
                    <input type="checkbox" checked={config.disableAttackReRolls} onChange={(e) => handleConfigChange('disableAttackReRolls', e.target.checked)} />
                </div>
                <div className="input-group">
                    <Tooltip
                        title={
                            <pre className="tooltip-pre">
                                <strong>On:</strong> The simulator never spends power re-rolls.
                                <br />
                                <strong>Off:</strong> If the initial power total misses the current A.T., the simulator applies the selected power re-roll strategy. <strong>Table Heuristic</strong> is the recommended default.
                            </pre>
                        }
                        arrow
                        enterDelay={500}
                        enterNextDelay={500}
                    >
                        <Typography className="input-label-with-tooltip">
                            Disable Power Re-Rolls
                            <InfoIcon className="info-icon-small" />
                        </Typography>
                    </Tooltip>
                    <input type="checkbox" checked={config.disablePowerReRolls} onChange={(e) => handleConfigChange('disablePowerReRolls', e.target.checked)} />
                </div>
                <div className="input-group">
                    <Tooltip
                        title={
                            <pre className="tooltip-pre">
                                <strong>On:</strong> Oversized power pools stay unchanged, even when they exceed the normal die-color limit.
                                <br />
                                <strong>Off:</strong> Extra dice upgrade to the next tier when a color exceeds its limit. Example: 7 red becomes 5 red and 1 black.
                            </pre>
                        }
                        arrow
                        enterDelay={500}
                        enterNextDelay={500}
                    >
                        <Typography className="input-label-with-tooltip">
                            Disable Power Dice Limits
                            <InfoIcon className="info-icon-small" />
                        </Typography>
                    </Tooltip>
                    <input type="checkbox" checked={config.disablePowerDiceLimits} onChange={(e) => handleConfigChange('disablePowerDiceLimits', e.target.checked)} />
                </div>
                <div className="input-group input-group-power-reroll-strategy">
                    <Tooltip
                        title={
                            <pre className="tooltip-pre">
                                <strong>Table Heuristic:</strong> Recommended default. Treats power re-rolls as simultaneous, protects value when you are close, and risks more dice only when you are clearly short.
                                <br />
                                <strong>Legacy Heuristic:</strong> Older simulator behavior kept mainly for comparing against historical results.
                                <br />
                                <strong>Optimal Strategy:</strong> Exact reference mode. Tests every legal simultaneous re-roll bundle and picks the one with the highest wound chance. Slowest option, best for spot checks and validation.
                            </pre>
                        }
                        arrow
                        enterDelay={500}
                        enterNextDelay={500}
                    >
                        <Typography className="input-label-with-tooltip">
                            Power Re-Roll Strategy
                            <InfoIcon className="info-icon-small" />
                        </Typography>
                    </Tooltip>
                    <SearchableSelect
                        value={config.powerReRollStrategy}
                        options={POWER_REROLL_STRATEGY_OPTIONS.map((strategy) => ({ value: strategy }))}
                        onChange={(value) => handleConfigChange('powerReRollStrategy', value as PowerReRollStrategy)}
                        ariaLabel="Power Re-Roll Strategy"
                        className="settings-searchable-select settings-searchable-select-right"
                    />
                </div>
                <div className="spacer" />
                <div className="input-section-button-container">
                    <Button
                        className="reset-button"
                        variant="danger"
                        fullWidth
                        onClick={() =>
                            setConfig({
                                ...Constants.DEFAULT_SIMULATION_CONFIG,
                            })
                        }
                    >
                        Reset Config
                    </Button>
                </div>
            </div>
        </div>
    );
};
